// src/utils/klingeln.js
//
// Klingelton fuer den Countdown, komplett im Browser erzeugt (Web Audio API) –
// keine Sounddatei, keine Netzanbindung noetig.
//
// WICHTIG: Browser (vor allem iOS/Safari) erlauben Ton erst, nachdem der Nutzer
// die Seite angetippt hat. Deshalb muss `audioFreischalten()` direkt im
// Klick-Handler von "Start" laufen; spaeter, wenn der Countdown ablaeuft, ist
// der AudioContext dann bereits entsperrt und `klingeln()` darf von einem Timer
// aus starten.
//
// GRENZEN: Es klingelt nur, solange die Seite im Vordergrund und das Display an
// ist (beim Sperren pausiert der Browser Ton und Timer). Auf dem iPhone schaltet
// der Klingeln/Lautlos-Schalter Web-Audio stumm; Vibration gibt es dort gar
// nicht, auf Android schon.

let ctx = null;

function holeContext() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/** Im Klick-Handler aufrufen, damit der Ton spaeter ueberhaupt erlaubt ist. */
export function audioFreischalten() {
  const c = holeContext();
  if (c && c.state === "suspended") c.resume().catch(() => {});
}

// Wie ein klassischer Rasselwecker: Ein Kloeppel schlaegt etwa 20 Mal pro Sekunde
// gegen die Glocke, jeder Schlag klingt kurz aus. Das ergibt das "Brrrring" –
// weicher als ein Rechteck-Piepen, weil nur Sinus-Anteile gemischt werden.
const GLOCKE_HZ = 1175;
const OBERTON_FAKTOR = 2.4; // unharmonischer Oberton, macht den Klang metallisch
const SCHLAEGE_JE_SALVE = 20;
const SCHLAG_ABSTAND_S = 0.05;
const SALVE_DAUER_S = SCHLAEGE_JE_SALVE * SCHLAG_ABSTAND_S;
const SALVE_ABSTAND_MS = 1600;
// Hoechstens so viele Durchlaeufe, dann ist von selbst Ruhe. Vorher lief
// es zwoelf Sekunden (acht Durchlaeufe) – am Quiz-Tablet zu lang und laestig.
const MAX_SALVEN = 5;

function glockenSalve(c, startZeit) {
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, startZeit);
  for (let i = 0; i < SCHLAEGE_JE_SALVE; i++) {
    const t = startZeit + i * SCHLAG_ABSTAND_S;
    // Kurzer Anschlag, dann ausklingen – kein hartes Ein/Aus, also kein Knacken.
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.3, t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + SCHLAG_ABSTAND_S - 0.004);
  }
  gain.connect(c.destination);
  [[GLOCKE_HZ, 1], [GLOCKE_HZ * OBERTON_FAKTOR, 0.35]].forEach(([hz, pegel]) => {
    const osc = c.createOscillator();
    const lautstaerke = c.createGain();
    osc.type = "sine";
    osc.frequency.value = hz;
    lautstaerke.gain.value = pegel;
    osc.connect(lautstaerke).connect(gain);
    osc.start(startZeit);
    osc.stop(startZeit + SALVE_DAUER_S + 0.02);
  });
}

/**
 * Startet den Klingelton (Wecker-Rasseln, hoechstens MAX_SALVEN Mal) und
 * vibriert, wo moeglich. Gibt eine Funktion zurueck, die ihn sofort beendet.
 * `beiEnde` wird aufgerufen, wenn er nach dem letzten Durchlauf von selbst verstummt.
 */
export function klingeln(beiEnde) {
  const c = holeContext();
  let beendet = false;
  let salvenTimer = null;
  let endeTimer = null;
  let salven = 0;

  const salve = () => {
    if (beendet || salven >= MAX_SALVEN) return;
    salven++;
    if (c) {
      if (c.state === "suspended") c.resume().catch(() => {});
      const t0 = c.currentTime + 0.02;
      glockenSalve(c, t0);
    }
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate([60, 40, 60, 40, 60, 40, 60, 40, 60, 40, 60, 40, 60, 40, 60, 40, 60]);
    }
  };

  const stop = () => {
    if (beendet) return;
    beendet = true;
    clearInterval(salvenTimer);
    clearTimeout(endeTimer);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(0);
  };

  salve();
  salvenTimer = setInterval(salve, SALVE_ABSTAND_MS);
  // Ende = letzter Durchlauf gestartet + seine Dauer.
  endeTimer = setTimeout(() => {
    stop();
    if (beiEnde) beiEnde();
  }, (MAX_SALVEN - 1) * SALVE_ABSTAND_MS + SALVE_DAUER_S * 1000);
  return stop;
}
